import { Link } from "react-router";
import {
  Building2, Coins, DoorOpen, KeyRound, Lock, UserX,
} from "lucide-react";
import {
  PURCHASE_POINTS_ACCOUNT_LOCKED_MESSAGE,
  PURCHASE_POINTS_EMPLOYEE_DEPARTED_MESSAGE,
  PURCHASE_POINTS_OPTED_OUT_ACTION_LABEL,
  PURCHASE_POINTS_OPTED_OUT_MESSAGE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import type { PurchasePointsAccessCode } from "@/features/purchasePoints/errors/purchasePointsError";
import { USER_PROFILE_AUTORISATIONS_TAB } from "@/features/userProfile/routes";

type PurchasePointsAccessStateProps = {
  code: PurchasePointsAccessCode
  detail: string | null
};

type AccessState = {
  icon: typeof Lock
  fallbackMessage: string
  // Only the refusal the employee caused himself has somewhere to send him: the
  // others are decided above him and offer nothing to act on.
  action?: { label: string, to: string }
};

/**
 * These cases are not outages but access states: presenting them as a
 * technical error would suggest an incident and push the employee to retry for
 * nothing.
 */
const ACCESS_STATES: Record<PurchasePointsAccessCode, AccessState> = {
  cashback_disabled: {
    icon: Lock,
    fallbackMessage: "Ce service n'est pas disponible",
  },
  organization_feature_disabled: {
    icon: Building2,
    fallbackMessage: "Cette fonctionnalité n'est pas disponible pour votre organisation",
  },
  consumer_not_onboarded: {
    icon: UserX,
    fallbackMessage: "Votre compte n'est pas encore rattaché au service",
  },
  employee_opted_out: {
    icon: Coins,
    fallbackMessage: PURCHASE_POINTS_OPTED_OUT_MESSAGE,
    action: { label: PURCHASE_POINTS_OPTED_OUT_ACTION_LABEL, to: USER_PROFILE_AUTORISATIONS_TAB },
  },
  account_locked: {
    icon: KeyRound,
    fallbackMessage: PURCHASE_POINTS_ACCOUNT_LOCKED_MESSAGE,
  },
  employee_departed: {
    icon: DoorOpen,
    fallbackMessage: PURCHASE_POINTS_EMPLOYEE_DEPARTED_MESSAGE,
  },
};

export const PurchasePointsAccessState = ({ code, detail }: PurchasePointsAccessStateProps) => {
  const { icon: Icon, fallbackMessage, action } = ACCESS_STATES[code];

  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-12 text-center"
      data-testid={`purchase-points-access-state-${code}`}
    >
      <Icon size={48} className="text-gray-400 dark:text-gray-500" aria-hidden="true" />
      {/* The live region covers the message alone: a focusable element inside one
          is read out with it instead of being reached by normal navigation. */}
      <p role="status" className="text-gray-600 dark:text-gray-300">{detail ?? fallbackMessage}</p>
      {action && (
        <Link
          to={action.to}
          className="text-primary underline underline-offset-4"
          data-testid={`purchase-points-access-action-${code}`}
        >
          {action.label}
        </Link>
      )}
    </div>
  );
};

PurchasePointsAccessState.displayName = "PurchasePointsAccessState";
